import { Injectable } from '@angular/core';
import { ImpersonationSession } from '@app/core/models';

@Injectable({ providedIn: 'root' })
export class StorageService {
  private readonly tokenKey = 'action-ledgers.token';
  private readonly refreshTokenKey = 'action-ledgers.refresh-token';
  private readonly propertyKey = 'action-ledgers.property';
  private readonly clientAccountKey = 'action-ledgers.client-account';
  private readonly profileKeyPrefix = 'action-ledgers.profile';
  private readonly impersonationKey = 'action-ledgers.impersonation';
  private readonly userTourKeyPrefix = 'action-ledgers.user-tour';

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  clearToken(): void {
    localStorage.removeItem(this.tokenKey);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.refreshTokenKey);
  }

  setRefreshToken(token: string): void {
    localStorage.setItem(this.refreshTokenKey, token);
  }

  clearRefreshToken(): void {
    localStorage.removeItem(this.refreshTokenKey);
  }

  getSelectedPropertyId(): string | null {
    return localStorage.getItem(this.propertyKey);
  }

  setSelectedPropertyId(propertyId: string): void {
    localStorage.setItem(this.propertyKey, propertyId);
  }

  clearSelectedPropertyId(): void {
    localStorage.removeItem(this.propertyKey);
  }

  getSelectedClientAccountId(): string | null {
    return localStorage.getItem(this.clientAccountKey);
  }

  setSelectedClientAccountId(clientAccountId: string): void {
    localStorage.setItem(this.clientAccountKey, clientAccountId);
  }

  clearSelectedClientAccountId(): void {
    localStorage.removeItem(this.clientAccountKey);
  }

  getOwnerProfileDraft(userId: string): { fullName: string; phone: string } | null {
    try {
      const raw = localStorage.getItem(`${this.profileKeyPrefix}.${userId}`);
      return raw ? (JSON.parse(raw) as { fullName: string; phone: string }) : null;
    } catch {
      return null;
    }
  }

  setOwnerProfileDraft(userId: string, value: { fullName: string; phone: string }): void {
    localStorage.setItem(`${this.profileKeyPrefix}.${userId}`, JSON.stringify(value));
  }

  clearOwnerProfileDraft(userId: string): void {
    localStorage.removeItem(`${this.profileKeyPrefix}.${userId}`);
  }

  getImpersonationSession(): ImpersonationSession | null {
    try {
      const raw = localStorage.getItem(this.impersonationKey);
      return raw ? (JSON.parse(raw) as ImpersonationSession) : null;
    } catch {
      return null;
    }
  }

  setImpersonationSession(value: ImpersonationSession): void {
    localStorage.setItem(this.impersonationKey, JSON.stringify(value));
  }

  clearImpersonationSession(): void {
    localStorage.removeItem(this.impersonationKey);
  }

  getUserTourState<T>(userId: string, tourId: string): T | null {
    try {
      const raw = localStorage.getItem(this.userTourKey(userId, tourId));
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }

  setUserTourState<T>(userId: string, tourId: string, value: T): void {
    localStorage.setItem(this.userTourKey(userId, tourId), JSON.stringify(value));
  }

  clearUserTourState(userId: string, tourId: string): void {
    localStorage.removeItem(this.userTourKey(userId, tourId));
  }

  private userTourKey(userId: string, tourId: string): string {
    return `${this.userTourKeyPrefix}.${encodeURIComponent(userId)}.${encodeURIComponent(tourId)}`;
  }
}
